import { API_CONFIG } from '../config/api.config';
import {
  buildOwnerViewSmsMessage,
  normalizeSmsMsisdn,
} from '../utils/sms-owner-message';

/**
 * @typedef {Object} SendOwnerNotificationParams
 * @property {string} ownerPhone
 * @property {string} [ownerName]
 * @property {string} [buyerName]
 * @property {string} buyerPhone
 * @property {string} [propertyTitle]
 * @property {string} [propertyLocation] — ignored for DLT body (title only)
 */

/**
 * Send SMS to property owner when buyer views contact (Show Owner Details).
 * Infinitio `SendSMS` rejects GET; use POST with query string + empty body (native `fetch`, no axios).
 * @param {SendOwnerNotificationParams} params
 * @returns {Promise<boolean>}
 */
export async function sendOwnerViewNotification(params) {
  console.log('[SMS Service] ========== START SMS (Infinitio) ==========');

  try {
    if (!API_CONFIG.INFINITIO_SMS_API_KEY) {
      console.warn(
        '[SMS Service] INFINITIO_SMS_API_KEY is empty — add REACT_APP_INFINITIO_SMS_API_KEY to `.env` and restart the dev server.',
      );
      return false;
    }

    const ownerMsisdn = normalizeSmsMsisdn(params.ownerPhone);
    if (!ownerMsisdn || ownerMsisdn.length < 12) {
      console.warn('[SMS Service] Owner phone missing or invalid — skipping SMS.', ownerMsisdn);
      return false;
    }

    const buyerBody = String(params.buyerPhone || '').replace(/\D/g, '');
    if (!buyerBody) {
      console.warn('[SMS Service] Buyer phone missing — DLT body will be incomplete; skipping SMS.');
      return false;
    }

    const text = buildOwnerViewSmsMessage({
      ownerName: params.ownerName,
      buyerPhone: params.buyerPhone,
      propertyTitle: params.propertyTitle,
      dltSuffix: API_CONFIG.INFINITIO_SMS_DLT_MESSAGE_SUFFIX,
    });
    console.log('[SMS Service] DLT message:', text);
    console.log('[SMS Service] SMS length (chars):', text.length);

    const templateId = String(API_CONFIG.INFINITIO_SMS_TEMPLATE_ID ?? '').trim();
    const query = new URLSearchParams({
      APIkey: API_CONFIG.INFINITIO_SMS_API_KEY,
      senderid: API_CONFIG.INFINITIO_SMS_SENDER_ID,
      channel: API_CONFIG.INFINITIO_SMS_CHANNEL,
      DCS: API_CONFIG.INFINITIO_SMS_DCS,
      flashsms: API_CONFIG.INFINITIO_SMS_FLASHSMS,
      number: ownerMsisdn,
      text,
      route: API_CONFIG.INFINITIO_SMS_ROUTE,
    });
    if (templateId) {
      query.set('templateid', templateId);
    }

    const url = `${API_CONFIG.INFINITIO_SMS_SEND_URL}?${query.toString()}`;
    console.log(
      '[SMS Service] fetch POST SendSMS:',
      API_CONFIG.INFINITIO_SMS_SEND_URL,
      '| mode:',
      templateId ? `with templateid (${templateId})` : 'without templateid',
      '| number:',
      ownerMsisdn,
    );

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        Accept: 'application/json, text/plain, */*',
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: '',
      redirect: 'manual',
    });

    const responseText = await response.text();
    console.log('[SMS Service] HTTP', response.status, responseText?.slice(0, 500));

    if (response.status < 200 || response.status >= 300) {
      return false;
    }

    const trimmed = responseText.trim();
    if (trimmed.includes("does not support http method 'GET'")) {
      console.warn(
        "[SMS Service] Server responded as if the request was GET. That only happens for GET; the app uses POST. " +
          'If you opened the API URL in a browser or Postman with GET, use POST instead.',
      );
      return false;
    }
    if (!trimmed) {
      return true;
    }

    try {
      const data = JSON.parse(trimmed);
      if (typeof data.success === 'boolean') {
        return data.success;
      }
      const ec = String(data.ErrorCode ?? '').trim();
      const okCodes = ['0', '00', '000'];
      if (okCodes.includes(ec)) {
        return true;
      }
      const em = String(data.ErrorMessage ?? '').toLowerCase();
      if (em === 'success' && (ec === '' || okCodes.includes(ec))) {
        return true;
      }
      if (data.ErrorCode !== undefined && ec !== '' && !okCodes.includes(ec)) {
        console.warn('[SMS Service] Infinitio error:', data.ErrorCode, data.ErrorMessage);
        return false;
      }
    } catch {
      const lower = trimmed.toLowerCase();
      if (lower.includes('fail') || lower.includes('error') || lower.includes('invalid')) {
        return false;
      }
    }

    return true;
  } catch (error) {
    console.error('[SMS Service] Exception:', error?.message || error);
    return false;
  } finally {
    console.log('[SMS Service] ========== END SMS (Infinitio) ==========');
  }
}

/**
 * @param {SendOwnerNotificationParams} params
 * @returns {Promise<boolean>}
 */
export async function sendOwnerViewNotificationMock(params) {
  const text = buildOwnerViewSmsMessage({
    ownerName: params.ownerName,
    buyerPhone: params.buyerPhone,
    propertyTitle: params.propertyTitle,
    dltSuffix: API_CONFIG.INFINITIO_SMS_DLT_MESSAGE_SUFFIX,
  });
  console.log('[MOCK SMS] Would send to owner:', params.ownerPhone, text);
  await new Promise((r) => setTimeout(r, 300));
  return true;
}
